import React from 'react';
import { motion } from 'framer-motion';
import { 
  AlertTriangle, 
  CheckCircle, 
  Info, 
  Shield,
  AlertCircle,
  Clock,
  TrendingUp,
  Database
} from 'lucide-react';

interface DataQualityIndicatorProps {
  quality: number;
  flags: string[];
  severity: 'low' | 'medium' | 'high';
  className?: string;
}

const DataQualityIndicator: React.FC<DataQualityIndicatorProps> = ({ 
  quality, 
  flags, 
  severity, 
  className = '' 
}) => {
  const getQualityLevel = () => {
    if (quality >= 80) return { level: 'Excellent', color: 'emerald', icon: CheckCircle };
    if (quality >= 60) return { level: 'Good', color: 'blue', icon: Info };
    if (quality >= 40) return { level: 'Fair', color: 'amber', icon: Clock };
    return { level: 'Poor', color: 'red', icon: AlertTriangle };
  };

  const getSeverityColor = () => {
    switch (severity) {
      case 'high': return 'text-red-600 bg-red-100 border-red-200';
      case 'medium': return 'text-amber-600 bg-amber-100 border-amber-200';
      case 'low': return 'text-blue-600 bg-blue-100 border-blue-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getFlagDescription = (flag: string) => {
    const descriptions = {
      'no_data': 'No cycle data available',
      'insufficient_period_data': 'Need more period start dates for accurate analysis',
      'unrealistic_cycle_lengths': 'Some cycle lengths appear unusually short or long',
      'highly_irregular_cycles': 'Cycles show high variability',
      'inconsistent_tracking': 'Gaps detected in tracking consistency',
      'contradictory_period_flow_data': 'Period days with inconsistent flow information',
      'unrealistic_symptom_consistency': 'Symptoms reported with unusual consistency',
      'future_dates_detected': 'Future dates found in cycle logs',
      'duplicate_dates': 'Multiple entries for the same date detected'
    };
    return descriptions[flag as keyof typeof descriptions] || flag;
  };

  const qualityInfo = getQualityLevel();
  const IconComponent = qualityInfo.icon;

  return (
    <motion.div
      className={`bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-200 p-4 ${className}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-3">
          <motion.div
            className={`p-2 bg-gradient-to-br from-${qualityInfo.color}-500 to-${qualityInfo.color}-600 rounded-lg`}
            whileHover={{ scale: 1.1, rotate: 5 }}
          >
            <Database className="w-5 h-5 text-white" />
          </motion.div>
          <div>
            <h3 className="font-semibold text-gray-900">Data Quality</h3>
            <p className="text-sm text-gray-600">{qualityInfo.level} ({quality}%)</p>
          </div>
        </div>
        
        <motion.div
          className={`flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium border ${getSeverityColor()}`}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2 }}
        >
          <IconComponent className="w-3 h-3" />
          <span>{severity} priority</span>
        </motion.div>
      </div>

      {/* Quality Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Quality Score</span>
          <span className="font-medium">{quality}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <motion.div
            className={`bg-gradient-to-r from-${qualityInfo.color}-500 to-${qualityInfo.color}-600 h-2 rounded-full`}
            initial={{ width: 0 }}
            animate={{ width: `${quality}%` }}
            transition={{ delay: 0.3, duration: 1 }}
          />
        </div>
      </div>

      {/* Data Quality Flags */}
      {flags.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-2">Data Quality Notes:</h4>
          <div className="space-y-2">
            {flags.slice(0, 3).map((flag, index) => (
              <motion.div
                key={flag}
                className="flex items-start space-x-2 text-sm"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
              >
                <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{getFlagDescription(flag)}</span>
              </motion.div>
            ))}
            {flags.length > 3 && (
              <p className="text-xs text-gray-500 ml-6">
                +{flags.length - 3} more issue{flags.length - 3 !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Improvement Tips */}
      {quality < 80 && (
        <motion.div
          className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <div className="flex items-start space-x-2">
            <TrendingUp className="w-4 h-4 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900 mb-1">Quick Improvement Tips:</p>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>• Mark period start dates accurately</li>
                <li>• Track consistently without large gaps</li>
                <li>• Verify dates are not in the future</li>
                <li>• Include flow levels on period days</li>
              </ul>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default DataQualityIndicator;